import React from 'react';
import { useLocation } from '../context/LocationContext';

/**
 * Clean inline SVG location pin icon; renders consistently across all platforms.
 */
function LocationIcon({ size = 16, className = '' }) {
  return (
    <svg
      className={`location-bar-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * LocationBar - persistent bar below the header showing the user's service area.
 * For logged-in users it displays the saved location from signup.
 * The detect button is available for manual re-detection when needed.
 */
export default function LocationBar() {
  const { location, locationLoading, locationError, detectLocation } = useLocation();

  const cityLabel = location?.city || null;
  const isSaved = location?.source === 'saved';

  return (
    <div className="location-bar" role="region" aria-label="Service area">
      <div className="location-bar-inner" aria-live="polite">
        {locationLoading ? (
          <span className="location-bar-status location-bar-status--detecting">
            <LocationIcon size={14} className="location-bar-dot" />
            <span className="location-bar-status-copy">
              <span className="location-bar-status-title">Checking service area</span>
              <span className="location-bar-helper">Finding nearby workers and coverage</span>
            </span>
          </span>
        ) : locationError && !location ? (
          <span className="location-bar-status location-bar-status--pending">
            <span className="location-bar-status-copy">
              <span className="location-bar-status-title">Service area not set</span>
              <span className="location-bar-helper">Use location to show nearby workers</span>
            </span>
            <button type="button" className="location-bar-btn" onClick={detectLocation}>
              <LocationIcon size={14} /> Use my location
            </button>
          </span>
        ) : (
          <>
            {cityLabel && (
              <button
                type="button"
                className="location-bar-city"
                onClick={detectLocation}
                title={isSaved ? 'Saved service area. Click to update.' : 'Detected service area. Click to update.'}
              >
                <LocationIcon size={14} className="location-bar-dot" />
                <span className="location-bar-name">{cityLabel}</span>
                <span className="location-bar-change">{isSaved ? 'Saved area' : 'Update'}</span>
              </button>
            )}
            {!cityLabel && (
              <span className="location-bar-status location-bar-status--pending">
                <span className="location-bar-status-copy">
                  <span className="location-bar-status-title">Set service area</span>
                  <span className="location-bar-helper">Better matches, price, and availability</span>
                </span>
                <button type="button" className="location-bar-btn" onClick={detectLocation} title="Use my current location">
                  <LocationIcon size={14} /> Use my location
                </button>
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
